import React, { useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Podcasts as PodcastsIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import type { PodcastData } from '../contexts/PodcastsContext';
import { parseOrDefaultPodcastEpisode } from '../portal/podcastEpisodeEnvelope';

export interface PodcastListItemProps {
  podcast: PodcastData;
  onOpen: (podcast: PodcastData) => void;
  onArchive: (e: React.MouseEvent, podcast: PodcastData) => void;
  onTrash: (e: React.MouseEvent, podcast: PodcastData) => void;
  onRestore: (e: React.MouseEvent, podcast: PodcastData) => void;
  onDelete: (e: React.MouseEvent, podcast: PodcastData) => void;
  onToggleReady: (podcast: PodcastData) => void | Promise<void>;
  divider?: boolean;
}

const PodcastListItem: React.FC<PodcastListItemProps> = ({
  podcast,
  onOpen,
  onArchive,
  onTrash,
  onRestore,
  onDelete,
  onToggleReady,
  divider = false,
}) => {
  const [readyBusy, setReadyBusy] = useState(false);
  const id = podcast.id ?? '';
  const episode = parseOrDefaultPodcastEpisode(
    podcast.content,
    id || 'podcast',
    podcast.title,
    podcast.summary
  );
  const ready = Boolean(episode.ready_for_publishing);
  const status = (podcast.status || 'active') as 'active' | 'archived' | 'trash';

  const thumbUrl = (() => {
    const u = (episode.image || '').trim();
    if (!u) return null;
    try {
      const x = new URL(u);
      return x.protocol === 'http:' || x.protocol === 'https:' ? u : null;
    } catch {
      return null;
    }
  })();

  const editedSource = podcast.updatedAt || podcast.createdAt;
  const editedMs = editedSource ? new Date(editedSource).getTime() : null;
  const lastEditedLabel =
    editedMs != null && !Number.isNaN(editedMs) ? format(editedMs, 'MMM d, yyyy h:mm a') : '—';

  const canMutate = Boolean(podcast.id);

  const handleReadyClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canMutate || readyBusy || ready) return;
    setReadyBusy(true);
    try {
      await onToggleReady(podcast);
    } finally {
      setReadyBusy(false);
    }
  };

  return (
    <ListItemButton
      onClick={() => onOpen(podcast)}
      disabled={!canMutate}
      sx={{
        borderBottom: divider ? '1px solid #27272a' : 'none',
        bgcolor: '#0a0a0a',
        mb: 1,
        borderRadius: 1,
        py: 1.5,
        px: 2,
        border: '1px solid #27272a',
        flexDirection: 'column',
        alignItems: 'stretch',
        '&:hover': { bgcolor: '#1a1a1a' },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Box display="flex" gap={1.5} minWidth={0} flex={1}>
          {thumbUrl ? (
            <Box
              component="img"
              src={thumbUrl}
              alt=""
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                objectFit: 'cover',
                flexShrink: 0,
                mt: 0.25,
                border: '1px solid #27272a',
              }}
            />
          ) : (
            <PodcastsIcon sx={{ fontSize: 28, color: '#c084fc', flexShrink: 0, mt: 0.25 }} />
          )}
          <Box minWidth={0}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#fafafa' }} noWrap>
              {podcast.title}
            </Typography>
            <Typography variant="body2" noWrap sx={{ color: '#a1a1aa' }}>
              {podcast.summary || 'No summary available'}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit">
            <span>
              <IconButton
                size="small"
                disabled={!canMutate}
                onClick={() => onOpen(podcast)}
                sx={{ bgcolor: '#27272a', color: '#fafafa', '&:hover': { bgcolor: '#3f3f46' } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {status === 'active' && (
            <Tooltip title={ready ? 'Ready to publish — locked' : 'Mark ready to publish (cannot be undone)'}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canMutate || readyBusy || ready}
                  onClick={handleReadyClick}
                  sx={{
                    bgcolor: ready ? '#d1fae5' : '#27272a',
                    color: ready ? '#047857' : '#a1a1aa',
                    '&:hover': { bgcolor: ready ? '#d1fae5' : '#3f3f46' },
                  }}
                >
                  {ready ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {status === 'active' && (
            <Tooltip title="Archive">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMutate}
                  onClick={(e) => onArchive(e, podcast)}
                  sx={{ bgcolor: '#27272a', color: '#a1a1aa', '&:hover': { bgcolor: '#3f3f46', color: '#fafafa' } }}
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {status === 'archived' && (
            <Tooltip title="Restore from archive">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMutate}
                  onClick={(e) => onRestore(e, podcast)}
                  sx={{ bgcolor: '#27272a', color: '#60a5fa', '&:hover': { bgcolor: '#3f3f46' } }}
                >
                  <UnarchiveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {status === 'trash' && (
            <Tooltip title="Restore">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMutate}
                  onClick={(e) => onRestore(e, podcast)}
                  sx={{ bgcolor: '#27272a', color: '#60a5fa', '&:hover': { bgcolor: '#3f3f46' } }}
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {status !== 'trash' && (
            <Tooltip title="Move to trash">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMutate}
                  onClick={(e) => onTrash(e, podcast)}
                  sx={{ bgcolor: '#27272a', color: '#f87171', '&:hover': { bgcolor: '#3f3f46', color: '#ef4444' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {status === 'trash' && (
            <Tooltip title="Delete permanently">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMutate}
                  onClick={(e) => onDelete(e, podcast)}
                  sx={{ bgcolor: '#450a0a', color: '#fecaca', '&:hover': { bgcolor: '#7f1d1d' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 1.25, borderColor: '#27272a' }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="caption" sx={{ color: '#71717a' }}>
          Last edited {lastEditedLabel}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={0.75} justifyContent="flex-end">
          {status !== 'active' && (
            <Chip
              label={status === 'archived' ? 'Archived' : 'Trash'}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                bgcolor: status === 'trash' ? '#450a0a' : '#27272a',
                color: status === 'trash' ? '#fecaca' : '#a1a1aa',
              }}
            />
          )}
          {podcast.duration != null && podcast.duration > 0 && (
            <Chip
              label={`${podcast.duration} min`}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#1e40af', color: '#dbeafe' }}
            />
          )}
          {Boolean(podcast.language) && (
            <Chip
              label={podcast.language}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#581c87', color: '#f3e8ff' }}
            />
          )}
          {podcast.visibility === 'public' ? (
            <Chip
              label="Public"
              size="small"
              icon={<VisibilityIcon sx={{ fontSize: '14px !important' }} />}
              sx={{ height: 22, fontSize: '0.7rem', borderColor: '#4ade80', color: '#4ade80' }}
              variant="outlined"
            />
          ) : (
            <Chip
              label="Private"
              size="small"
              icon={<LockIcon sx={{ fontSize: '14px !important' }} />}
              sx={{ height: 22, fontSize: '0.7rem', borderColor: '#a1a1aa', color: '#e4e4e7' }}
              variant="outlined"
            />
          )}
          {ready && (
            <Chip label="Ready" size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#14532d', color: '#bbf7d0' }} />
          )}
          {Boolean(podcast.notebookName) && (
            <Chip
              label={podcast.notebookName}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem', borderColor: '#60a5fa', color: '#60a5fa' }}
            />
          )}
        </Box>
      </Box>
    </ListItemButton>
  );
};

export default PodcastListItem;
